import type { ApiErrorBody } from "./types";

export class ApiError extends Error {
  status: number;
  code?: string;
  /** What the refusal carried beyond the sentence, when it carried anything. */
  details?: Record<string, unknown>;

  constructor(status: number, message: string, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export type ApiClient = {
  baseUrl: string;
  request: <T>(path: string, init?: RequestInit & { json?: unknown }) => Promise<T>;
  /** Fetch a file through the API with the bearer token and hand it to the browser as a download. */
  download: (path: string, filename?: string) => Promise<void>;
};

export const createApiClient = (options: {
  baseUrl: string;
  getToken: () => string | undefined;
}): ApiClient => {
  const request = async <T>(
    path: string,
    init: RequestInit & { json?: unknown } = {}
  ): Promise<T> => {
    const headers = new Headers(init.headers);
    const token = options.getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    let body = init.body;
    if (init.json !== undefined) {
      headers.set("Content-Type", "application/json");
      body = JSON.stringify(init.json);
    }
    const response = await fetch(`${options.baseUrl}${path}`, { ...init, headers, body });
    if (response.status === 204) {
      return undefined as T;
    }
    const text = await response.text();
    const data = text ? (JSON.parse(text) as unknown) : undefined;
    if (!response.ok) {
      const err = (data ?? {}) as Partial<ApiErrorBody> & { details?: Record<string, unknown> };
      throw new ApiError(response.status, err.message ?? `Request failed (${response.status})`, err.code, err.details);
    }
    return data as T;
  };

  const download = async (path: string, filename?: string): Promise<void> => {
    const headers = new Headers();
    const token = options.getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const response = await fetch(`${options.baseUrl}${path}`, { headers });
    if (!response.ok) {
      let message = `Download failed (${response.status})`;
      try {
        const body = (await response.json()) as Partial<ApiErrorBody>;
        if (body.message) message = body.message;
      } catch {
        // not JSON
      }
      throw new ApiError(response.status, message);
    }
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const fromHeader = /filename="?([^";]+)"?/.exec(disposition)?.[1];
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename ?? fromHeader ?? path.split("/").pop()?.split("?")[0] ?? "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  return { baseUrl: options.baseUrl, request, download };
};

/** Upload a file straight to S3 using a presigned PUT from the API. */
export const uploadToPresignedUrl = async (
  uploadUrl: string,
  file: Blob,
  headers: Record<string, string>
): Promise<void> => {
  const response = await fetch(uploadUrl, { method: "PUT", body: file, headers });
  if (!response.ok) {
    throw new ApiError(response.status, "Upload to storage failed");
  }
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[i]}`;
};
